import { AutocompleteSelect } from '@/components/ui/AutocompleteSelect'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Box, HStack, IconButton } from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiSearch, FiX } from 'react-icons/fi'
import { leadsApi } from '../api/leadsApi'
import { getSourceOptions, getStateOptions, getStatusOptions } from '../constants/leadEnums'

/**
 * Leads Filters Component
 * Filtering controls for leads list
 */
export function LeadsFilters({ filters, onFiltersChange, onReset }) {
  const { t } = useTranslation()
  // Local state for search input (submit on Enter only)
  const [searchValue, setSearchValue] = useState(filters.search || '')

  const handleChange = (field, value) => {
    onFiltersChange({ ...filters, [field]: value })
  }

  const handleSearchSubmit = () => {
    handleChange('search', searchValue)
  }

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit()
    }
  }

  const handleReset = () => {
    setSearchValue('')
    onReset()
  }

  return (
    <HStack spacing={ 4 } flexWrap="wrap">
        {/* Search by name/phone */}
        <HStack minW="250px" flex={ 1 }>
          <Input
            placeholder={t('leads.searchByNameOrPhone')}
            value={ searchValue }
            onChange={ (e) => setSearchValue(e.target.value) }
            onKeyPress={ handleSearchKeyPress }
          />
          <IconButton
            aria-label="Search"
            onClick={ handleSearchSubmit }
            variant="outline"
          >
            <Box as={ FiSearch } />
          </IconButton>
        </HStack>

        {/* Status filter */}
        <Box minW="150px">
          <Select
            placeholder={t('leads.allStatuses')}
            value={ filters.status || '' }
            onChange={ (e) => handleChange('status', e.target.value) }
          >
            {getStatusOptions().map((option) => (
              <option key={ option.value } value={ option.value }>
                { option.label }
              </option>
            ))}
          </Select>
        </Box>

        {/* State filter */}
        <Box minW="150px">
          <Select
            placeholder={t('leads.allStates')}
            value={ filters.state || '' }
            onChange={ (e) => handleChange('state', e.target.value) }
          >
            {getStateOptions().map((option) => (
              <option key={ option.value } value={ option.value }>
                { option.label }
              </option>
            ))}
          </Select>
        </Box>

        {/* Source filter */}
        <Box minW="150px">
          <Select
            placeholder={t('leads.allSources')}
            value={ filters.source || '' }
            onChange={ (e) => handleChange('source', e.target.value) }
          >
            {getSourceOptions().map((option) => (
              <option key={ option.value } value={ option.value }>
                { option.label }
              </option>
            ))}
          </Select>
        </Box>

        {/* Assigned to filter */}
        <Box minW="200px">
          <AutocompleteSelect
            placeholder={t('leads.allAssigned')}
            value={filters.assigned_to}
            onChange={(value) => handleChange('assigned_to', value)}
            fetchOptions={leadsApi.getUsersAutocomplete}
            displayField={(user) => `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username}
            noOptionsMessage="No users found"
          />
        </Box>

        {/* Reset button */}
        <Button
          variant="ghost"
          onClick={ handleReset }
          disabled={ !Object.values(filters).some((v) => v) }
        >
          <HStack spacing={ 2 }>
            <Box as={ FiX } />
            <span>Reset</span>
          </HStack>
        </Button>
      </HStack>
  )
}

export default LeadsFilters
